import React, { useState, useEffect } from 'react';
import { Song } from '../../types';

interface SongMetadataModalProps {
  song: Song;
  isVisible: boolean;
  onClose: () => void;
  onSave: (metadata: Partial<Song>) => void;
}

export const SongMetadataModal: React.FC<SongMetadataModalProps> = ({
  song,
  isVisible,
  onClose,
  onSave
}) => {
  const [name, setName] = useState(song.name);
  const [artist, setArtist] = useState(song.artist || '');
  const [album, setAlbum] = useState(song.album || '');
  const [albumArtist, setAlbumArtist] = useState(song.albumArtist || '');
  const [genre, setGenre] = useState(song.genre || '');
  const [year, setYear] = useState(song.year?.toString() || '');
  const [trackNumber, setTrackNumber] = useState(song.trackNumber?.toString() || '');
  const [discNumber, setDiscNumber] = useState(song.discNumber?.toString() || '');
  const [composer, setComposer] = useState(song.composer || '');
  const [comment, setComment] = useState(song.comment || '');

  // Reset form when song changes
  useEffect(() => {
    setName(song.name);
    setArtist(song.artist || '');
    setAlbum(song.album || '');
    setAlbumArtist(song.albumArtist || '');
    setGenre(song.genre || '');
    setYear(song.year?.toString() || '');
    setTrackNumber(song.trackNumber?.toString() || '');
    setDiscNumber(song.discNumber?.toString() || '');
    setComposer(song.composer || '');
    setComment(song.comment || '');
  }, [song]);

  const handleSave = () => {
    const metadata: Partial<Song> = {
      name: name.trim(),
      artist: artist.trim() || null,
      album: album.trim() || null,
      albumArtist: albumArtist.trim() || null,
      genre: genre.trim() || null,
      year: year ? parseInt(year) : null,
      trackNumber: trackNumber ? parseInt(trackNumber) : null,
      discNumber: discNumber ? parseInt(discNumber) : null,
      composer: composer.trim() || null,
      comment: comment.trim() || null,
    };
    onSave(metadata);
    onClose();
  };

  const handleCancel = () => {
    // Reset to original values
    setName(song.name);
    setArtist(song.artist || '');
    setAlbum(song.album || '');
    setAlbumArtist(song.albumArtist || '');
    setGenre(song.genre || '');
    setYear(song.year?.toString() || '');
    setTrackNumber(song.trackNumber?.toString() || '');
    setDiscNumber(song.discNumber?.toString() || '');
    setComposer(song.composer || '');
    setComment(song.comment || '');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Song Metadata
          </h2>
          <button 
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Song title"
              />
            </div>

            {/* Artist */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Artist
              </label>
              <input
                type="text"
                value={artist}
                onChange={(e) => setArtist(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Artist name"
              />
            </div>

            {/* Album */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Album
              </label>
              <input
                type="text"
                value={album}
                onChange={(e) => setAlbum(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Album name"
              />
            </div>

            {/* Album Artist */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Album Artist
              </label>
              <input
                type="text"
                value={albumArtist}
                onChange={(e) => setAlbumArtist(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Album artist (if different from artist)"
              />
            </div>

            {/* Genre and Year */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Genre
                </label>
                <input
                  type="text"
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Rock, Pop, Jazz"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 2024"
                  min="1900"
                  max="2100"
                />
              </div>
            </div>

            {/* Track Number and Disc Number */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Track Number
                </label>
                <input
                  type="number"
                  value={trackNumber}
                  onChange={(e) => setTrackNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 1"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Disc Number
                </label>
                <input
                  type="number"
                  value={discNumber}
                  onChange={(e) => setDiscNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 1"
                  min="1"
                />
              </div>
            </div>

            {/* Composer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Composer
              </label>
              <input
                type="text"
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Composer name"
              />
            </div>

            {/* Comment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comment
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional notes or comments"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
